import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { signinCredentialsSchema } from "@monorepo/validation";
import {
  useSigninApi,
  formatErrorForDisplay,
  setTemporaryData,
  useGuestGuard,
  useAuth,
  isSigninLoginResponseMFA,
  isSigninPasswordChangeRequired,
  isSigninMfaSetupRequired,
  isSigninLoginResponseSuccess,
  isApiError,
  usePreventUnload,
  cookieManager,
} from "@monorepo/auth-utils";
import { STORAGE_KEYS, env, API_ENDPOINTS, ROUTES } from "@monorepo/config";
import { Button, TextInput, PasswordInput, ErrorBanner } from "@monorepo/ui";
// Use barrel import for consistency with other pages
import { SigninLayout } from "../../components";
import styles from "./SigninPage.module.scss";

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

type SigninCredentialsFormData = z.infer<typeof signinCredentialsSchema>;

// ----------------------------------------------------------------------
// CONSTANTS & CONFIGURATION
// ----------------------------------------------------------------------

const PAGE_CONFIG = {
  TURNSTILE_SITE_KEY:
    import.meta.env.VITE_TURNSTILE_SITE_KEY ||
    (import.meta.env.DEV ? "0x4AAAAAABCD8Y_MwifpZZRd" : ""),
} as const;

const PAGE_CONTENT = {
  TITLE: "Sign in to your account",
  SUBTITLE: "Enter your details to continue",
  LABELS: {
    EMAIL: "Email Address",
    PASSWORD: "Password",
    SUBMIT: "Sign In",
    FORGOT_PASSWORD: "Forgot password?",
  },
  PLACEHOLDERS: {
    EMAIL: "you@company.com",
    PASSWORD: "Enter your password",
  },
  MESSAGES: {
    SECURITY_CHECK_REQUIRED: "Please complete the security check.",
    SECURITY_CHECK_FAILED: "Security check failed. Please refresh.",
  },
  ERRORS: {
    INVALID_CREDENTIALS: "Invalid email or password.",
    ACCOUNT_LOCKED: "Your account has been locked. Please contact support.",
    RATE_LIMITED: "Too many attempts. Please wait a moment and try again.",
    GENERIC_SYSTEM: "System error. Please try again later.",
    GENERIC_FALLBACK: "An unexpected error occurred. Please try again.",
    EMPTY_RESPONSE: "Received empty or invalid response from server.",
  },
  LEGAL: {
    AGREE: "By continuing, you agree to our",
    TERMS: "Terms of Service",
    AND: "and",
    PRIVACY: "Privacy Policy",
  },
} as const;

// ----------------------------------------------------------------------
// COMPONENT
// ----------------------------------------------------------------------

export function SigninPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const { loginWithPassword, loading, clearError, getRedirectUrl } = useSigninApi();
  const { isAuthenticated } = useAuth();
  const isMobileFlow = !!searchParams.get("deviceid") || !!sessionStorage.getItem("oidc_auth_deviceid") || !!localStorage.getItem("oidc_auth_deviceid");

  // 4. Prevent accidental unloads
  const { disableUnloadWarning } = usePreventUnload(true);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [mobileTokens, setMobileTokens] = useState<any>(null);
  const [isProcessingOidc, setIsProcessingOidc] = useState(!!searchParams.get("code"));
  const justLoggedInRef = useRef(false);

  // 1. OIDC Self-Healing & Mobile Detection
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const deviceId = searchParams.get("deviceid");

    // Handle Mobile Device Detection
    if (deviceId) {
      sessionStorage.setItem("oidc_auth_deviceid", deviceId);
      // Also persist to localStorage as backup — Flutter WebView may lose
      // sessionStorage when redirecting through an external OIDC provider.
      localStorage.setItem("oidc_auth_deviceid", deviceId);
    }

    // NEW: Handle OIDC exchange directly on root to avoid 404 routing issues
    if (code) {
      const processOidc = async () => {
        if (import.meta.env.DEV) {
          console.info("[SigninPage] Processing OIDC code directly...");
        }
        
        try {
          // URL #1: Exchange code for session
          const callbackUrl = `${env.apiBaseUrl}${API_ENDPOINTS.OIDC_CALLBACK}?code=${code}&state=${state || ""}`;
          const response = await fetch(callbackUrl, { method: 'GET', credentials: 'include' });
          
          if (!response.ok) {
            throw new Error(`Callback API returned status ${response.status}`);
          }
          
          // ------------------------------------------------------------------
          // Mobile Handover Logic (Integrated from Callback page)
          // ------------------------------------------------------------------
          if (isMobileFlow) {
            const tokenUrl = `${env.apiBaseUrl}${API_ENDPOINTS.MOBILE_GET_ACCESS_TOKEN}`;
            const tokenResponse = await fetch(tokenUrl, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
            });
            
            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              setMobileTokens(tokenData);

              const payload = { status: "ok", ...tokenData };
              const message = JSON.stringify({ type: "AUTH_SUCCESS", payload: payload });
              if ((window as any).FlutterJSChannel) {
                (window as any).FlutterJSChannel.postMessage(message);
              } else {
                window.postMessage(message, "*");
              }

              if (import.meta.env.DEV) {
                console.info("[SigninPage] Mobile tokens seamlessly pushed to app.");
              }
            } else {
              console.error("[SigninPage] Mobile access token fetch failed.");
            }
            return; // Stop execution here so we do NOT redirect!
          }

          // URL #2: Standard Redirect (for web users or if bridge is missing/failed)
          getRedirectUrl();
        } catch (err) {
          console.error("[SigninPage] OIDC process failed:", err);
          if (isMounted.current) {
            setGlobalError("System error or network failure. Please check your connection and try again.");
            setIsProcessingOidc(false);
          }
        }
      };
      processOidc();
    }
  }, [searchParams, getRedirectUrl, isMobileFlow]);

  // 2. Security: Redirect logged-in users away (Disabled for mobile flow)
  useGuestGuard(undefined, navigate, isMobileFlow);

  // 3. Mobile Auto-Redirect: If user is already authenticated in a mobile flow, 
  // push them to OIDC immediately instead of showing the login form.
  useEffect(() => {
    const code = searchParams.get("code");
    if (isAuthenticated && isMobileFlow && !code && !justLoggedInRef.current) {
      if (import.meta.env.DEV) {
        console.info("[SigninPage] Authenticated mobile session detected. Forwarding to OIDC...");
      }
      getRedirectUrl();
    }
  }, [isAuthenticated, isMobileFlow, getRedirectUrl, searchParams]);

  // UX: Track widget load state to show spinner
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm<SigninCredentialsFormData>({
    resolver: zodResolver(signinCredentialsSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // UX: Handle widget loader state safely
  useEffect(() => {
    if ((window as any).turnstile) {
      setIsWidgetLoaded(true);
    }
    const timer = setTimeout(() => {
      if (isMounted.current) setIsWidgetLoaded(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Clear stale .sagasoft.xyz auth cookies on signin page load.
  // If the user lands here with existing auth cookies it means the session
  // was invalidated server-side (e.g. 302 redirect). We remove just the two
  // Authentik cookies so that re-authentication starts with a clean slate.
  useEffect(() => {
    // Skip if we are returning from OIDC flow, because we NEED these cookies for the callback!
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("code")) {
      return;
    }

    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const hostnameParts = hostname.split(".");
    const cookieDomain = hostnameParts.length >= 2 ? `.${hostnameParts.slice(-2).join(".")}` : "";

    const authCookiesToClear = [
      STORAGE_KEYS.AUTHENTIK_SESSION,
      STORAGE_KEYS.AUTHENTIK_CSRF,
    ] as const;

    let removedAny = false;
    for (const cookieName of authCookiesToClear) {
      if (cookieManager.get(cookieName)) {
        cookieManager.remove(cookieName, cookieDomain);
        removedAny = true;
      }
    }

    if (removedAny && import.meta.env.DEV) {
      console.info(
        "[SigninPage] Cleared stale .sagasoft.xyz auth cookies (session expired or 302 redirect)."
      );
    }
  }, []); // Intentionally runs only once on mount

  useEffect(() => {
    isMounted.current = true;
    setFocus("email");

    if (!PAGE_CONFIG.TURNSTILE_SITE_KEY && import.meta.env.DEV) {
      console.warn("Turnstile Site Key is missing in configuration.");
    }

    return () => {
      isMounted.current = false;
    };
  }, [setFocus]);

  const onSubmit = async (data: SigninCredentialsFormData) => {
    clearError();
    setGlobalError(null);

    if (!captchaToken) {
      setGlobalError(PAGE_CONTENT.MESSAGES.SECURITY_CHECK_REQUIRED);
      turnstileRef.current?.reset();
      return;
    }

    try {
      // Flag as just logged in to block auto-redirect useEffect
      justLoggedInRef.current = true;
      // 2. Call Login directly (Merged Flow)
      const response = await loginWithPassword({
        email: data.email,
        password: data.password,
        captchaToken,
        // FIX: Add device_name to satisfy backend requirement
        device_name: getDeviceName(),
      });

      if (!response) {
        throw new Error(PAGE_CONTENT.ERRORS.EMPTY_RESPONSE);
      }

      // Persist email for subsequent steps (MFA, Force Password Change, etc.)
      setTemporaryData("email", data.email);

      // SCENARIO 1: Forced Password Change (Employee First Login)
      if (isSigninPasswordChangeRequired(response)) {
        if (isMounted.current) {
          navigate(ROUTES.SIGNIN_FORCE_CHANGE_PASSWORD, {
            replace: true,
            state: {
              email: response.email,
              preAuthToken: response.pre_auth_token,
            },
          });
        }
        return;
      }

      // SCENARIO 2: Forced MFA Setup (Employee Onboarding)
      if (isSigninMfaSetupRequired(response)) {
        if (isMounted.current) {
          navigate(ROUTES.SIGNIN_MFA_SETUP, {
            replace: true,
            state: {
              email: data.email,
              preAuthToken: response.pre_auth_token,
            },
          });
        }
        return;
      }

      // SCENARIO 3: Standard MFA Verification
      if (isSigninLoginResponseMFA(response)) {
        setTemporaryData("email", data.email);

        const method =
          response.preferred || (response.methods && response.methods[0]);
        let nextRoute: string = ROUTES.SIGNIN_MFA;

        if (method === "totp") nextRoute = ROUTES.SIGNIN_MFA_TOTP;
        else if (method === "webauthn") nextRoute = ROUTES.SIGNIN_MFA_WEBAUTHN;
        // Also support backup code preference if returned
        else if (method === "backup_code") nextRoute = ROUTES.SIGNIN_MFA_BACKUP;

        if (isMounted.current) {
          navigate(nextRoute, {
            replace: true,
            state: {
              email: data.email,
              preMfaToken: response.pre_mfa_token,
              methods: response.methods,
            },
          });
        }
        return;
      }

      // SCENARIO 4: Success (Direct Login)
      if (isSigninLoginResponseSuccess(response)) {
        disableUnloadWarning();
        if (isMobileFlow) {
          try {
            const tokenUrl = `${env.apiBaseUrl}${API_ENDPOINTS.MOBILE_GET_ACCESS_TOKEN}`;
            const tokenResponse = await fetch(tokenUrl, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
            });
            
            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              if (isMounted.current) {
                setMobileTokens(tokenData);
              }

              const payload = { status: "ok", ...tokenData };
              const message = JSON.stringify({ type: "AUTH_SUCCESS", payload: payload });
              if ((window as any).FlutterJSChannel) {
                (window as any).FlutterJSChannel.postMessage(message);
              } else {
                window.postMessage(message, "*");
              }

              if (import.meta.env.DEV) {
                console.info("[SigninPage] Direct Mobile tokens seamlessly pushed to app.");
              }
            } else {
              console.error("[SigninPage] Direct Mobile access token fetch failed.");
              getRedirectUrl();
            }
          } catch (err) {
            console.error("[SigninPage] Direct Mobile access token fetch error:", err);
            getRedirectUrl();
          }
        } else {
          // Automatically trigger the redirect to Authentik/APISIX
          getRedirectUrl();
        }
      }
    } catch (error) {
      // Reset flag on error
      justLoggedInRef.current = false;
      if (import.meta.env.DEV) {
        console.error("Login error:", error);
      }

      turnstileRef.current?.reset();
      if (isMounted.current) {
        setCaptchaToken(null);
      }

      if (isApiError(error)) {
        switch (error.code) {
          case "INVALID_CREDENTIALS":
          case "USER_NOT_FOUND":
          case "INVALID_PASSWORD":
            setGlobalError(PAGE_CONTENT.ERRORS.INVALID_CREDENTIALS);
            break;
          case "ACCOUNT_LOCKED":
            setGlobalError(PAGE_CONTENT.ERRORS.ACCOUNT_LOCKED);
            break;
          case "RATE_LIMITED":
            setGlobalError(PAGE_CONTENT.ERRORS.RATE_LIMITED);
            break;
          default:
            const display = formatErrorForDisplay(error);
            setGlobalError(display.message);
        }
      } else {
        const msg = error instanceof Error ? error.message : "";
        setGlobalError(
          msg === PAGE_CONTENT.ERRORS.EMPTY_RESPONSE
            ? PAGE_CONTENT.ERRORS.GENERIC_SYSTEM
            : PAGE_CONTENT.ERRORS.GENERIC_FALLBACK
        );
      }
    }
  };


  if (isAuthenticated && isMobileFlow) {
    return (
      <div className={styles.widgetLoader} style={{ height: "100vh" }}>
        <div className={styles.spinner} />
        <p style={{ marginTop: "1rem", color: "var(--text-secondary)" }}>Redirecting to mobile session...</p>
      </div>
    );
  }

  if (mobileTokens) {
    return (
      <div className={styles.widgetLoader} style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div className={styles.spinner} style={{ width: "40px", height: "40px" }} />
      </div>
    );
  }

  if (isProcessingOidc) {
    return (
      <div className={styles.widgetLoader} style={{ height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <div className={styles.spinner} style={{ width: "40px", height: "40px", marginBottom: "1rem" }} />
        <p style={{ color: "var(--text-secondary)" }}>Finalizing authentication...</p>
      </div>
    );
  }

  return (
    <SigninLayout title={PAGE_CONTENT.TITLE} subtitle={PAGE_CONTENT.SUBTITLE}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
        noValidate
      >
        {globalError && (
          <ErrorBanner
            variant="error"
            title="Authentication Error"
            message={globalError}
            onDismiss={() => setGlobalError(null)}
          />
        )}

        <TextInput
          {...register("email")}
          data-testid="signin-email"
          label={PAGE_CONTENT.LABELS.EMAIL}
          type="email"
          placeholder={PAGE_CONTENT.PLACEHOLDERS.EMAIL}
          autoComplete="username email"
          required
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          error={errors.email?.message}
          disabled={isSubmitting || loading}
        />

        <div className={styles.passwordWrapper}>
          <PasswordInput
            {...register("password")}
            data-testid="signin-password"
            label={PAGE_CONTENT.LABELS.PASSWORD}
            placeholder={PAGE_CONTENT.PLACEHOLDERS.PASSWORD}
            autoComplete="current-password"
            required
            error={errors.password?.message}
            disabled={isSubmitting || loading}
          />
          <div className={styles.forgotPassword}>
            <a href="/account-recovery/" tabIndex={-1}>
              {PAGE_CONTENT.LABELS.FORGOT_PASSWORD}
            </a>
          </div>
        </div>

        <div className={styles.turnstileWrapper}>
          {!isWidgetLoaded && (
            <div className={styles.widgetLoader}>
              <div className={styles.spinner} />
            </div>
          )}

          <Turnstile
            ref={turnstileRef}
            siteKey={PAGE_CONFIG.TURNSTILE_SITE_KEY}
            style={{ width: "100%" }}
            options={{
              theme: "light",
              size: "flexible",
            }}
            onWidgetLoad={() => {
              if (isMounted.current) setIsWidgetLoaded(true);
            }}
            onSuccess={(token) => {
              if (isMounted.current) {
                setCaptchaToken(token);
                setIsWidgetLoaded(true);
              }
            }}
            onError={() => {
              if (isMounted.current) {
                setGlobalError(PAGE_CONTENT.MESSAGES.SECURITY_CHECK_FAILED);
                setCaptchaToken(null);
                setIsWidgetLoaded(true);
              }
            }}
            onExpire={() => {
              if (isMounted.current) setCaptchaToken(null);
            }}
          />
        </div>

        <Button
          type="submit"
          data-testid="signin-submit"
          variant="primary"
          fullWidth
          loading={loading || isSubmitting}
          disabled={loading || isSubmitting || !captchaToken}
        >
          {PAGE_CONTENT.LABELS.SUBMIT}
        </Button>

        <div className={styles.signupPrompt}>
          Don't have an account?{" "}
          <a href="/signup" className={styles.signupLink}>
            Sign up
          </a>
        </div>

        <p className={styles.terms}>
          {PAGE_CONTENT.LEGAL.AGREE}{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            {PAGE_CONTENT.LEGAL.TERMS}
          </a>{" "}
          {PAGE_CONTENT.LEGAL.AND}{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            {PAGE_CONTENT.LEGAL.PRIVACY}
          </a>
        </p>
      </form>
    </SigninLayout>
  );
}

function getDeviceName(): string {
  const ua = navigator.userAgent;
  let browser = "Unknown Browser";
  let os = "Unknown OS";

  if (ua.includes("Chrome") && !ua.includes("Edg")) {
    browser = "Chrome";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    browser = "Safari";
  } else if (ua.includes("Firefox")) {
    browser = "Firefox";
  } else if (ua.includes("Edg")) {
    browser = "Edge";
  }

  if (ua.includes("Windows")) {
    os = "Windows";
  } else if (ua.includes("Mac")) {
    os = "macOS";
  } else if (ua.includes("Linux")) {
    os = "Linux";
  } else if (ua.includes("Android")) {
    os = "Android";
  } else if (
    ua.includes("iOS") ||
    ua.includes("iPhone") ||
    ua.includes("iPad")
  ) {
    os = "iOS";
  }

  return `${browser} on ${os}`;
}
